<script setup>
import { computed, ref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import WootEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

const props = defineProps({
  // Takes { content, files } and resolves to whether the comment was saved, so a failed one
  // stays in the box.
  addComment: { type: Function, required: true },
});

const currentUser = useMapGetter('getCurrentUser');

const content = ref('');
// { file, previewUrl } — the preview only for an image.
const files = ref([]);
const isSending = ref(false);
// Enter picks an agent while the @mention list is open, instead of sending.
const isMentionOpen = ref(false);
const fileInput = ref(null);

const canSend = computed(
  () => (content.value.trim() || files.value.length) && !isSending.value
);

const addFiles = event => {
  [...event.target.files].forEach(file =>
    files.value.push({
      file,
      previewUrl: file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : null,
    })
  );
  event.target.value = '';
};

const removeFile = index => {
  const [removed] = files.value.splice(index, 1);
  if (removed.previewUrl) URL.revokeObjectURL(removed.previewUrl);
};

const submit = async () => {
  if (!canSend.value) return;
  isSending.value = true;
  const saved = await props.addComment({
    content: content.value.trim(),
    files: files.value.map(({ file }) => file),
  });
  if (saved) {
    content.value = '';
    files.value.forEach(
      ({ previewUrl }) => previewUrl && URL.revokeObjectURL(previewUrl)
    );
    files.value = [];
  }
  isSending.value = false;
};

// Enter sends and Shift+Enter breaks the line, as in Notion. Caught before the editor sees it.
const onKeydown = event => {
  if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
  if (isMentionOpen.value) return;
  event.preventDefault();
  event.stopPropagation();
  submit();
};
</script>

<template>
  <div class="flex items-start gap-3">
    <Avatar
      :name="currentUser.name || ''"
      :src="currentUser.avatar_url"
      :size="24"
      rounded-full
      class="mt-1.5"
    />
    <div
      class="flex flex-col flex-1 min-w-0 gap-2 px-3 py-1.5 border rounded-lg border-n-weak focus-within:border-n-brand"
      @keydown.capture="onKeydown"
    >
      <div class="flex items-end gap-2">
        <WootEditor
          v-model="content"
          is-private
          :enable-canned-responses="false"
          :focus-on-mount="false"
          :placeholder="$t('CARDS.SHOW.COMMENT_PLACEHOLDER')"
          class="flex-1 min-w-0 text-sm [&_.ProseMirror-menubar]:!hidden [&_.ProseMirror-woot-style]:!min-h-6 [&_.ProseMirror-woot-style]:!max-h-60"
          @toggle-user-mention="isMentionOpen = $event"
        />
        <div class="flex items-center flex-shrink-0 gap-1">
          <input
            ref="fileInput"
            type="file"
            multiple
            hidden
            data-testid="card-comment-file-input"
            @change="addFiles"
          />
          <Button
            icon="i-lucide-paperclip"
            slate
            ghost
            xs
            :title="$t('CARDS.SHOW.COMMENT_ATTACH')"
            data-testid="card-comment-attach"
            @click="fileInput.click()"
          />
          <Button
            icon="i-lucide-arrow-up"
            xs
            class="!rounded-full"
            :title="$t('CARDS.SHOW.COMMENT_SUBMIT')"
            data-testid="card-comment-submit"
            :is-loading="isSending"
            :disabled="!canSend"
            @click="submit"
          />
        </div>
      </div>
      <ul v-if="files.length" class="flex flex-wrap gap-2 mb-0">
        <li
          v-for="(item, index) in files"
          :key="index"
          class="relative flex items-center gap-2 overflow-hidden rounded-lg bg-n-alpha-1 text-n-slate-12"
          :class="item.previewUrl ? 'size-16' : 'h-8 pl-2 pr-8 max-w-60'"
          data-testid="card-comment-pending-file"
        >
          <img
            v-if="item.previewUrl"
            :src="item.previewUrl"
            :alt="item.file.name"
            class="object-cover size-full"
          />
          <template v-else>
            <Icon icon="i-lucide-file" class="flex-shrink-0 size-4" />
            <span class="text-xs truncate">{{ item.file.name }}</span>
          </template>
          <button
            type="button"
            class="absolute grid rounded-full top-1 ltr:right-1 rtl:left-1 size-5 place-items-center bg-n-alpha-black1 text-n-slate-12 hover:bg-n-alpha-black2"
            :aria-label="
              $t('CARDS.SHOW.COMMENT_REMOVE_FILE', { name: item.file.name })
            "
            data-testid="card-comment-remove-file"
            @click="removeFile(index)"
          >
            <Icon icon="i-lucide-x" class="size-3" />
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>
